import { AppContext } from "App";

import { ICryptographicInfo, IEncryptionInfo } from "Global/Base/Crypto/ICryptographicInfo";
import { DIContainer } from "Global/Base/DIContainer";
import { isVersionOlderOrEqual } from "Global/Base/Utils/versionUtils";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { CertificateUsageType, ICertificate, IDistinguishedName, ISignatureInfo, SignatureInfoType } from "../../../../Global/Base/Crypto/ISignatureInfo";
import { CancellationToken } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { CultureInfo } from "../../../../Global/Dom/CultureInfo";
import { ASServices } from "../../../../OW.Remoting/Basic/ServiceEndpoints";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { RequestContext } from "../../../../OW.Remoting/Remoting/RequestContext";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { DropDownMenuItemWithDetail } from "../../../UI.Controls/DropDownMenu/DropDownMenuItemWithDetail";
import { SeparatorMenuItem } from "../../../UI.Controls/DropDownMenu/SeparatorItem";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { SignatureCssClass, SignatureValidationMsg, UseCase } from "./CryptoInfoTypes";

export * from "./CryptoInfoTypes";

export class CryptoInfo {
    public static isEmpty(signatureInfo: ISignatureInfo | null | undefined): boolean {
        return !signatureInfo || signatureInfo.type === SignatureInfoType.empty;
    }

    public static isSigned(signatureInfo: ISignatureInfo | null | undefined): boolean {
        return signatureInfo && signatureInfo.type !== SignatureInfoType.empty;
    }

    public static isEncrypted(cryptoInfo: ICryptographicInfo | null | undefined): boolean {
        if (cryptoInfo?.encryptionInfo?.encrypted) {
            return true;
        }
        const validationMessage = cryptoInfo?.signatureInfo?.validationMessage as SignatureValidationMsg;
        if ([SignatureValidationMsg.IsEncrypted, SignatureValidationMsg.AttributeEncrypted, SignatureValidationMsg.AllowUnknownSignature].includes(validationMessage)) {
            return true;
        }
        return false;
    }

    public static hasSignatureWarning(signatureInfo: ISignatureInfo | null | undefined, withCounterSignatures = true): boolean {
        if (!signatureInfo) {
            return false;
        }
        if (withCounterSignatures && signatureInfo.counterSignature) {
            for (const cs of signatureInfo.counterSignature)
                if (CryptoInfo.hasSignatureWarning(cs))
                    return true;
        }
        if (signatureInfo.valid == null && signatureInfo.validationMessage && signatureInfo.validationMessage.length && signatureInfo.validationMessage != SignatureValidationMsg.EmptySignature) {
            return true;
        } else if (signatureInfo.valid === false && signatureInfo.validationMessage) {
            switch (signatureInfo.validationMessage) {
                case SignatureValidationMsg.Failed:
                case SignatureValidationMsg.Error:
                case SignatureValidationMsg.Empty:
                    break;
                default:
                    return true;
            }
        } else if (signatureInfo.valid === false) {
            return true;
        }
        return false;
    }

    public static getSignatureWarningClass(signatureInfo: ISignatureInfo, withCounterSignatures = true): SignatureCssClass | null {
        if (!CryptoInfo.hasSignatureWarning(signatureInfo, withCounterSignatures))
            return null;

        return signatureInfo.type === SignatureInfoType.unknown ? SignatureCssClass.Unknown : SignatureCssClass.Warning;
    }

    public static async getCryptoInfoToolTip(signatureInfo: ISignatureInfo, encInfo?: IEncryptionInfo): Promise<string> {
        const textLines: string[] = [];
        if (encInfo && encInfo.encrypted) {
            const encTooltipParts = await CryptoInfo._getEncryptionTooltipParts(encInfo);
            const encTooltip = `${await Localization.getTextPromise("GUI-536875")}: ${encTooltipParts.length ? encTooltipParts.join(", ") : ""}  `; //Zašifrováno:
            textLines.push(encTooltip);
        }
        if (!signatureInfo)
            textLines.push(await Localization.getTextPromise("GUI-389642")); // Digitální podpis je prázdný
        else {
            textLines.push(await CryptoInfo.createSignatureCertificateTooltipText(signatureInfo, CultureInfo.getCultureInfo().name)); //control.Culture);
            textLines.push(await CryptoInfo._getSignatureStateTooltip(signatureInfo));
            textLines.push(await CryptoInfo._getCounterSignatureCountTooltip(signatureInfo));
        }

        return textLines.filter(l => l).join("\n"); //vyfiltruje null nebo undefined řádky a spojí je.
    }

    public static async getCryptoInfoDropDown(cryptoInfo: ICryptographicInfo, obj: OWObject = null, attrName: string = "", _ct?: CancellationToken): Promise<DropDownMenu> {
        const signatureInfoMenu = new DropDownMenu();

        //informace o šifrování
        if (cryptoInfo.encryptionInfo?.encrypted) {
            await CryptoInfo._createEncryptionInfoDropDown(cryptoInfo.encryptionInfo, signatureInfoMenu);
            signatureInfoMenu.addItem(new SeparatorMenuItem());
        }
        //informace o podpisu
        await CryptoInfo._creatSignatureInfoDropDown(obj, attrName, cryptoInfo.signatureInfo, signatureInfoMenu);
        return signatureInfoMenu;
    }

    private static async _createEncryptionInfoDropDown(encryptionInfo: IEncryptionInfo, signatureInfoMenu: DropDownMenu): Promise<void> {
        const caption = await Localization.getTextPromise("GUI-536875"); //Zašifrováno
        const subtextParts = await CryptoInfo._getEncryptionTooltipParts(encryptionInfo); //datum a čas šifry, název certifikátu
        subtextParts.push(`${await Localization.getTextPromise("GUI-536869")}: ${encryptionInfo.algorithm}`); //Algoritmus šifrování: <alg>
        const encryptionDetail = await CryptoInfo.createCertificateDetailBody(encryptionInfo.certificate);
        const headerEncryption = DropDownMenuItemWithDetail.createDropDownMenuItemWithDetail("none", caption, subtextParts.join("\n"), "", encryptionDetail, true);
        signatureInfoMenu.addItem(headerEncryption);
    }

    private static async _creatSignatureInfoDropDown(obj: OWObject, attrName: string, signatureInfo: ISignatureInfo, signatureInfoMenu: DropDownMenu): Promise<void> {
        const isSigned = signatureInfo && CryptoInfo.isSigned(signatureInfo);
        const signature = await Localization.getTextPromise("GUI-462916"); // Podpis
        const signatureState = await CryptoInfo._getSignatureStateTooltip(signatureInfo);

        //detail podpisu
        let signatureDetail: HTMLElement = undefined;
        if (obj && isSigned) {
            signatureDetail = createElement();

            const btnDownloadSignature = createElement({ classes: "btn-download" });
            Localization.addLocTxtData(btnDownloadSignature, "GUI-629759"); // stáhnout podpis záznamu
            btnDownloadSignature.addEventListener("click", function() {
                const useLegacyApi = isVersionOlderOrEqual(AppContext.instance.configuration.getDevelopmentOption("webServiceVersion"), "15.03");
                let handlerUrlPart: string;
                if (attrName == "")
                    handlerUrlPart = `${useLegacyApi ? "mwclient/ExportSignedObject.aspx" : "api/v01/as/exports/obj-signature"}?InstanceID=${obj.instanceID}`;
                else
                    handlerUrlPart = `${useLegacyApi ? "mwclient/ExportSignedDocument.aspx" : "api/v01/as/exports/doc-signature"}?InstanceID=${obj.instanceID}&AttributeName=${attrName}`;

                const url = `${AppContext.instance.addresses.serviceUrl}/${handlerUrlPart}`;
                window.open(url, "BinaryData", "toolbar=no,status=no,menubar=no,location=no,resizable=yes,scrollbars=yes");
            });

            signatureDetail.appendChild(btnDownloadSignature);
        }
        const header = DropDownMenuItemWithDetail.createDropDownMenuItemWithDetail("none", signature, signatureState, "", signatureDetail, true);
        if (CryptoInfo.hasSignatureWarning(signatureInfo) || CryptoInfo.isEncrypted({ signatureInfo }))
            header.setSubtextColor("orange");
        else if (!CryptoInfo.isEmpty(signatureInfo))
            header.setSubtextColor("green");
        signatureInfoMenu.addItem(header);
        if (isSigned)
            await CryptoInfo._createSignatureMenuItems(signatureInfo, signatureInfoMenu);
    }
    private static async _createSignatureMenuItems(signatureInfo: ISignatureInfo, signatureInfoMenu: DropDownMenu): Promise<void> {
        signatureInfoMenu.addItem(new SeparatorMenuItem());
        signatureInfoMenu.addItem(await CryptoInfo._createSignatureDetail(signatureInfo));
        for (let i = 0; i < signatureInfo.counterSignature.length; i++) {
            await CryptoInfo._createSignatureMenuItems(signatureInfo.counterSignature[i], signatureInfoMenu);
        }
    }

    private static async _createSignatureDetail(signatureInfo: ISignatureInfo): Promise<DropDownMenuItem> {
        const culture = CultureInfo.getCultureInfo().name;
        let icon = "none";
        switch (signatureInfo.type) {
            case SignatureInfoType.digitalSignature:
                icon = "editorial-pen";
                break;
            case SignatureInfoType.timeStamp:
                icon = "time-clock-circle";
                break;
            case SignatureInfoType.digitalMark:
                icon = "user-lock";
                break;
        }

        const caption = signatureInfo.certificate ? CryptoInfo.formatDistinguishedNamePart(signatureInfo.certificate.subject, "CN") : "";

        const warning = CryptoInfo.hasSignatureWarning(signatureInfo, false);
        let warningText = "";
        if (warning)
            warningText = await CryptoInfo.getSignatureState(signatureInfo);

        const date = signatureInfo.time ? new Date(signatureInfo.time.toString()).toLocaleDateString(culture) + ", " + new Date(signatureInfo.time.toString()).toLocaleTimeString(culture) : ""; //.toLocaleString(culture);

        const detail = await CryptoInfo.createCertificateDetailBody(signatureInfo.certificate, warning, warningText);

        const signatureDropDown = DropDownMenuItemWithDetail.createDropDownMenuItemWithDetail(icon, caption, date, "", detail, true);
        signatureDropDown.setExpandIcon();
        if (CryptoInfo.hasSignatureWarning(signatureInfo, false) || CryptoInfo.isEncrypted({ signatureInfo })) {
            signatureDropDown.setWarningIcon();
        } else {
            signatureDropDown.setOkIcon();
        }

        if (detail.childNodes.length == 0)
            signatureDropDown.isEnabledExpand = false;

        signatureDropDown.addEventListener("click", () => {
            if (!signatureDropDown.classList.contains("expanded")) {
                const expanded = signatureDropDown.parentElement.querySelector(".expanded");
                if (expanded)
                    expanded.classList.remove("expanded");
            }
        });

        return signatureDropDown;
    }

    public static async createCertificateDetailBody(certificateInfo: ICertificate, warning: boolean = false, warningText: string = ""): Promise<HTMLElement> {
        const detail = createElement({ classes: "detail" });
        const culture = CultureInfo.getCultureInfo().name;

        if (certificateInfo) {
            const dateFrom = new Date(certificateInfo.validFrom.toString()).toLocaleDateString(culture);
            const dateTo = new Date(certificateInfo.validTo.toString()).toLocaleDateString(culture);

            const clientSignatureTimeInfo = createElement({ classes: "detail-item" });
            Localization.addLocTxtData(clientSignatureTimeInfo, "GUI-653208"); //Čas podepsání pochází z hodin na počítači autora podpisu.
            detail.appendChild(clientSignatureTimeInfo);

            if (warning)
                detail.appendChild(CryptoInfo._createSignatureDetailItem("", warningText, " orange"));
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629011", await CryptoInfo._getSignatureUsageString(certificateInfo))); // Účel certifikátu
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629012", certificateInfo.serialNumber)); // Sériové číslo
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629013", CryptoInfo.formatDistinguishedNamePart(certificateInfo.issuer, "CN"))); // Vystavovatel
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629014", certificateInfo.signatureAlgorithm)); // Algoritmus podpisu
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629015", dateFrom + " - " + dateTo)); // Platnost
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629016", certificateInfo.hasPrivateKey ? await Localization.getTextPromise("GUI-146838") : await Localization.getTextPromise("GUI-146840"))); // Obsahuje privátní klíč ... Ano / Ne
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629017", certificateInfo.version.toString())); // Verze
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629018", certificateInfo.publicKeyAlgorithm)); // Veřejný klíč
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629019", certificateInfo.hashAlgorithm)); // Algoritmus kryptografického otisku
            detail.appendChild(CryptoInfo._createSignatureDetailItem("GUI-629020", certificateInfo.thumbprint)); // Kryptografický otisk
        } else {
            if (warning) {
                detail.appendChild(CryptoInfo._createSignatureDetailItem("", warningText, " orange"));
            }
        }
        return detail;
    }

    private static async _getSignatureUsageString(certificateInfo: ICertificate): Promise<string> {
        let certificateUsage: string[] = [];
        const certificateUsagePromise: Promise<string>[] = [];
        if (certificateInfo.certificateKeyUsage) {
            for (const usage of certificateInfo.certificateKeyUsage) {
                let key = "";
                switch (usage) {
                    case CertificateUsageType.crlSign:
                        key = "GUI-488336";
                        break; // Podepisování CRL
                    case CertificateUsageType.dataEncipherment:
                        key = "GUI-488340";
                        break; // Šifrování dat
                    case CertificateUsageType.decipherOnly:
                        key = "GUI-488342";
                        break; // Dešifrování
                    case CertificateUsageType.digitalSignature:
                        key = "GUI-488346";
                        break; // Digitální podpis
                    case CertificateUsageType.encipherOnly:
                        key = "GUI-488348";
                        break; // Šifrování
                    case CertificateUsageType.keyAgreement:
                        key = "GUI-488352";
                        break; // Dohoda o klíčích
                    case CertificateUsageType.keyCertSign:
                        key = "GUI-488354";
                        break; // Podepisování certifikátu
                    case CertificateUsageType.keyEncipherment:
                        key = "GUI-488356";
                        break; // Šifrování klíče
                    //case CertificateUsageType.none: key = "GUI-630047"; break; // Žádné parametry použití klíče
                    case CertificateUsageType.nonRepudiation:
                        key = "GUI-488358";
                        break; // Neodvolatelnost
                    default:
                        key = "";
                        break;
                }
                if (key != "")
                    certificateUsagePromise.push(Localization.getTextPromise(key));
            }
        }
        certificateUsage = await Promise.all(certificateUsagePromise);
        return certificateUsage.join(", ");
    }

    private static _createSignatureDetailItem(captionKey: string, value: string, valueClass: string = ""): HTMLElement {
        const item = createElement({ classes: "detail-item" });

        if (captionKey != "") {
            const captionEl = createElement({ classes: "caption" });
            Localization.addLocTxtData(captionEl, captionKey);
            item.appendChild(captionEl);
        }
        const valueEl = createElement({ classes: "value" + valueClass });
        valueEl.innerText = value;
        item.appendChild(valueEl);

        return item;
    }

    public static async _getSignatureStateTooltip(info: ISignatureInfo): Promise<string> {
        if (info == null)
            return null;

        // null - prázdný, neznámý, šifrovaný podpis
        if (info.valid == null) {
            if (info.validationMessage && info.validationMessage.length)
                return await CryptoInfo.getSignatureValidationText(info.validationMessage);
            else
                return await Localization.getTextPromise("GUI-389642"); // Digitální podpis je prázdný
        } else if (info.valid === true) {
            // root signature is valid, but one of the countersignatures might not be
            if (CryptoInfo.hasSignatureWarning(info, true)) {
                return await CryptoInfo.getSignatureValidationText(SignatureValidationMsg.CantVerifySignature); // Nepodařilo se ověřit platnost součásti podpisu.
            }
            return await Localization.getTextPromise("GUI-389872"); // Digitální podpis je platný
        } else {
            switch (info.validationMessage) {
                case null:
                case "":
                    return await Localization.getTextPromise("GUI-389876"); // Chyba při kontrole digitálního podpisu
                case SignatureValidationMsg.Failed:
                case SignatureValidationMsg.Error:
                case SignatureValidationMsg.Empty:
                    return "";
                default:
                    return await CryptoInfo.getSignatureValidationText(info.validationMessage);
            }
        }
    }

    public static async getSignatureState(info: ISignatureInfo): Promise<string> {
        if (info == null)
            return null;

        // null - prázdný, neznámý, šifrovaný podpis
        if (info.valid == null) {
            return CryptoInfo.getSignatureValidationText(info.validationMessage);
        } else if (info.valid === true) { // true - platný
            return await Localization.getTextPromise("GUI-389872"); // Digitální podpis je platný
        } else {
            const message = "";
            return `${await Localization.getTextPromise("GUI-389876")}\n${message}`; //'Chyba při kontrole digitálního podpisu
        }
    }

    /**
     * Ze struktury názvu vybere požadovanou část a pokud jich je více, tak je spojí oddělovačem do jednoho řetězce
     * @param nameInfo Struktura s názvem
     * @param partKey Klíč názvu hledané části
     * @param separator Oddělovač použitý pro spojení více částí
     * @param defaultReturnValue Výchozí hodnota vrácená při neexistenci části
     */
    public static formatDistinguishedNamePart(nameInfo: IDistinguishedName, partKey: string, separator: string = ", ", defaultReturnValue: any = ""): string | null {
        const parts = nameInfo[partKey];
        return parts ? parts.join(separator) : defaultReturnValue;
    }

    /**
     * Vytvoří text pro tooltip podpisu
     * @param signatureInfo Informace o podpisu
     * @param culture Kultura použitá pro formátování
     */
    public static async createSignatureCertificateTooltipText(signatureInfo: ISignatureInfo, culture: string): Promise<string> {
        if (!signatureInfo || signatureInfo.certificate == null)
            return null;

        const parts: string[] = [];

        parts.push(new Date(signatureInfo.time.toString()).toLocaleString(culture));

        const commonName = CryptoInfo.formatDistinguishedNamePart(signatureInfo.certificate.subject, "CN");
        if (commonName)
            parts.push(commonName);

        const email = CryptoInfo.formatDistinguishedNamePart(signatureInfo.certificate.subject, "E");
        if (email)
            parts.push(`(${email})`);

        const locText = await Localization.getTextPromise("GUI-536877"); //Podepsáno

        return `${locText}: ${parts.join(", ")}`; // `
    }

    private static async _getCounterSignatureCountTooltip(signatureInfo: ISignatureInfo): Promise<string> {
        if (signatureInfo == null || signatureInfo.certificate == null)
            return null;

        if (signatureInfo.counterSignatureCount > 0) {
            return `${await Localization.getTextPromise("GUI-604239")}: ${signatureInfo.counterSignatureCount}`; //Počet kontrapodpisů
        } else if (signatureInfo.counterSignature.length > 0) {
            let count = 0;
            for (const signature of signatureInfo.counterSignature) {
                if (signature.type != SignatureInfoType.timeStamp)
                    count++;
            }
            return `${await Localization.getTextPromise("GUI-604239")}: ${count}`; //Počet kontrapodpisů
        }

        return null;
    }
    private static async _getEncryptionTooltipParts(cryptoInfo: IEncryptionInfo): Promise<string[]> {
        if (!cryptoInfo || !cryptoInfo.encrypted)
            return null;
        else {
            const texts: string[] = [];
            if (cryptoInfo.time) {
                const culture = CultureInfo.getCultureInfo().name;
                texts.push(cryptoInfo.time.toLocaleString(culture));
            }
            if (cryptoInfo.certificate && cryptoInfo.certificate.subject)
                texts.push(CryptoInfo.formatDistinguishedNamePart(cryptoInfo.certificate.subject, "CN"));
            return texts;
        }
    }

    /*
    * Vrací hlášku k zadanému stavu podpisu
    */
    public static getSignatureValidationText(validationMessage: string, useCase: UseCase = UseCase.None): Promise<string> {
        switch (useCase) {
            case UseCase.None:
                switch (validationMessage) {
                    case SignatureValidationMsg.SignaturePolicyNotMet: return Localization.getTextPromise("GUI-664969"); // Certifikát nesplňuje požadované vlastnosti
                    case SignatureValidationMsg.AllowUnknownSignature: return Localization.getTextPromise("GUI-618548");// Neznámý formát podpisu.
                    case SignatureValidationMsg.EmptySignature: return Localization.getTextPromise("GUI-389642"); // Digitální podpis je prázdný
                    case SignatureValidationMsg.HashEqualError: return Localization.getTextPromise("GUI-393912"); // Aktuální stav záznamu neodpovídá podpisu
                    case SignatureValidationMsg.IncorrectIssuer: return Localization.getTextPromise("GUI-582035"); // Chyba při kontrole certifikátu nesprávný vydavatel.
                    case SignatureValidationMsg.IncorrectKeyUsage: return Localization.getTextPromise("GUI-582034"); // Chyba při kontrole certifikátu nesprávné použití klíče.
                    case SignatureValidationMsg.IncorrectIssuerServerCertificate: return Localization.getTextPromise("GUI-614076"); // Chyba serverového certifikátu nepodporovaný vydavatel. Kontaktujte správce systému.
                    case SignatureValidationMsg.InvalidReferences: return Localization.getTextPromise("GUI-393912"); // Aktuální stav záznamu neodpovídá podpisu
                    case SignatureValidationMsg.InvalidSignatureType: return Localization.getTextPromise("GUI-568137"); // Neznámý typ podpisu
                    case SignatureValidationMsg.AttributeEncrypted:
                    case SignatureValidationMsg.IsEncrypted:
                        return Localization.getTextPromise("GUI-418020"); // Stav podpisu není možné ověřit data jsou šifrována.
                    case SignatureValidationMsg.NeedManualVerification: return Localization.getTextPromise("GUI-582043"); // Chyba při kontrole certifikátu certifikát nebylo možné ověřit.
                    case SignatureValidationMsg.OnlyDocumentPartSigned: return Localization.getTextPromise("GUI-568139"); // Pouze část dokumentu je podepsaná
                    case SignatureValidationMsg.OutOfDate: return Localization.getTextPromise("GUI-582040"); // Chyba při kontrole certifikátu nesprávné datum platnosti.
                    case SignatureValidationMsg.OutOfDateServerCertificate: return Localization.getTextPromise("GUI-614074"); // Chyba serverového certifikátu nesprávné datum platnosti. Kontaktujte správce systému.
                    case SignatureValidationMsg.Revocated: return Localization.getTextPromise("GUI-582041"); // Chyba při kontrole certifikátu certifikát byl odvolán.
                    case SignatureValidationMsg.RevocatedServerCertificate: return Localization.getTextPromise("GUI-614075"); // Chyba serverového certifikátu certifikát byl odvolán. Kontaktujte správce systému.
                    case SignatureValidationMsg.ServerCertificateError: return Localization.getTextPromise("GUI-614072"); // Chyba serverového certifikátu. Kontaktujte správce systému.
                    case SignatureValidationMsg.TimeStampError: return Localization.getTextPromise("GUI-582042"); // Chyby při kontrole certifikátu nesprávné časové razítko.
                    case SignatureValidationMsg.UnknownSignature: return Localization.getTextPromise("GUI-618548"); // Neznámý formát podpisu.
                    case SignatureValidationMsg.CantVerifySignature: return Localization.getTextPromise("GUI-564883"); // Nepodařilo se ověřit platnost součásti podpisu.
                    case SignatureValidationMsg.TryLater: return Localization.getTextPromise("GUI-658906"); // Aktuálně není možno certifikát použitý pro podpis ověřit. Ověření je třeba opakovat.
                    case SignatureValidationMsg.Error: return Localization.getTextPromise("GUI-564883"); // Nepodařilo se ověřit platnost součásti podpisu.
                    default:
                        break;
                }
            // falls through
            case UseCase.Signing:
                switch (validationMessage) {
                    case SignatureValidationMsg.SignaturePolicyNotMet: return Localization.getTextPromise("GUI-664969"); // Certifikát nesplňuje požadované vlastnosti
                    case SignatureValidationMsg.IncorrectIssuer: return Localization.getTextPromise("GUI-582035"); // Chyba při kontrole certifikátu nesprávný vydavatel.
                    case SignatureValidationMsg.IncorrectKeyUsage: return Localization.getTextPromise("GUI-582034"); // Chyba při kontrole certifikátu nesprávné použití klíče.
                    case SignatureValidationMsg.OutOfDate: return Localization.getTextPromise("GUI-582040"); // Chyba při kontrole certifikátu nesprávné datum platnosti.
                    case SignatureValidationMsg.Revocated: return Localization.getTextPromise("GUI-582041"); // Chyba při kontrole certifikátu certifikát byl odvolán.
                    case SignatureValidationMsg.TimeStampError: return Localization.getTextPromise("GUI-582042"); // Chyby při kontrole certifikátu nesprávné časové razítko.
                    case SignatureValidationMsg.CantVerifySignature: return Localization.getTextPromise("GUI-564883"); // Nepodařilo se ověřit platnost součásti podpisu.
                    case SignatureValidationMsg.TryLater: return Localization.getTextPromise("GUI-658906"); // Aktuálně není možno certifikát použitý pro podpis ověřit. Ověření je třeba opakovat.
                    case SignatureValidationMsg.Error: return Localization.getTextPromise("GUI-389142"); //  Při ověření certifikátu došlo k neočekávané chybě. Záznam nelze podepsat. Kontaktujte správce systému.
                    case SignatureValidationMsg.Failed: return Localization.getTextPromise("GUI-389144"); //Zadaný certifikát není možné použít pro podpis.
                    default:
                        break;
                }
        }
        return Promise.resolve(validationMessage);
    }

    /**
     * Vrací informace o šifování a podpisu objektu
     * @param instanceID Instance objektu
     * @param requestContext Viz {@link RequestContext}
     */
    public static getCryptographicInfo(instanceID: string, requestContext?: RequestContext): Promise<ICryptographicInfo> {
        const communicationManager = DIContainer.instance.create(ICommunicationManager);
        return communicationManager.callGroupedGETRequest("GetCryptographicInfo", ASServices.cryptography, { instanceID: instanceID }, undefined, undefined, requestContext).then((info: ICryptographicInfo) => {
            if (info && info.encryptionInfo && info.encryptionInfo.time)
                info.encryptionInfo.time = new Date(info.encryptionInfo.time);
            return info;
        });
    }
}
